import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MediaItem } from '@/types/media';
import { MEDIA_TYPE_LABEL } from '@/types/media';

export default function HeroCarousel({ items }: { items: MediaItem[] }) {
  const [index, setIndex] = useState(0);

  const go = useCallback(
    (next: number) => setIndex((next + items.length) % items.length),
    [items.length],
  );

  useEffect(() => {
    const timer = window.setInterval(() => setIndex((prev) => (prev + 1) % items.length), 5500);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const active = items[index];

  return (
    <section className="relative w-full rounded-lg overflow-hidden bg-secondary-950" aria-label="焦点视频轮播">
      <div className="relative w-full h-[260px] sm:h-[340px] lg:h-[420px]">
        {items.map((item, i) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}
            aria-hidden={i !== index}
          >
            <img
              src={item.backdrop}
              alt={`${item.title} 焦点推荐`}
              title={`${item.title} 高清视频观看`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-secondary-950 via-secondary-950/60 to-secondary-950/10" />

        <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-bold whitespace-nowrap">
              {MEDIA_TYPE_LABEL[active.type]}
            </span>
            <span className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs font-medium backdrop-blur whitespace-nowrap">
              {active.region}
            </span>
            <span className="inline-flex items-center gap-1 text-primary-300 text-sm font-bold">
              <i className="ri-star-fill"></i>
              {active.rating.toFixed(1)}
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-black text-background-50 mb-2">{active.title}</h2>
          <p className="text-sm md:text-base text-background-200/90 mb-1 line-clamp-2 max-w-2xl">{active.synopsis}</p>
          <p className="hidden sm:block text-xs text-background-300/70 mb-4">
            {active.genres.join(' · ')} · {active.episodes}
          </p>
          <div className="flex items-center gap-3 mt-2">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </Link>
            <Link
              to={`/category/${active.type}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-medium backdrop-blur hover:bg-background-50/25 transition-colors whitespace-nowrap cursor-pointer"
            >
              更多同类
            </Link>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/20 text-background-50 backdrop-blur flex items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer"
          aria-label="上一张"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/20 text-background-50 backdrop-blur flex items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer"
          aria-label="下一张"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>
      </div>

      <div className="absolute bottom-4 right-4 md:right-8 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到 ${item.title}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-7 bg-primary-400' : 'w-1.5 bg-background-50/40 hover:bg-background-50/70'
            }`}
          />
        ))}
      </div>
    </section>
  );
}